// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import BillingAddressInfoDisplay, {
    BillingAddressInfoDisplayStyles,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/BillingAddressInfoDisplay";
import BillingAndShippingInfo from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/BillingAndShippingInfo";
import PickUpLocation from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/PickUpLocation";
import { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CheckoutMoveToStep from "@pace/App/Components/Checkout/CheckoutMoveToStep";
import SectionTitle from "@pace/App/Components/Common/SectionTitle";
import { CustomAppState } from "@pace/App/Store/Reducers";
import { CHECKOUT_STEPS } from "@pace/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { getPaymentMethodValue, parseBoolean } from "@pace/XDL/Utilities";
import parse from "html-react-parser";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const { fulfillmentMethod, pickUpWarehouse } = state.context.session;
    // XNG-Changes
    const paymentMethodValue = getPaymentMethodValue(state.xCheckout.payment);
    return {
        cartId,
        cart,
        shipToState: getShipToState(state, cart?.shipToId),
        billToState: getBillToState(state, cart?.billToId),
        fulfillmentMethod,
        pickUpWarehouse,
        shippingPageNavLink: getPageLinkByPageType(state, "CheckoutShippingPage"),
        // XNG-Changes
        poNumber: state.xCheckout.payment.poNumber,
        jobName: state.xCheckout.payment.jobName,
        paymentMethodValue,
    };
};

const mapDispatchToProps = {
    loadShipTo,
    loadBillTo,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & WidgetProps & HasHistory;

export interface CheckoutReviewAndSubmitShippingInfoStyles {
    accordion?: AccordionPresentationProps;
    shippingInfoAccordionSection?: AccordionSectionPresentationProps;
    warningSection?: InjectableCss;
    warningIcon?: IconPresentationProps;
    warningText?: TypographyPresentationProps;
    // XNG-Changes
    wrapper?: InjectableCss;
    container?: InjectableCss;
    section?: InjectableCss;
    sectionTwoColums?: InjectableCss;
    sectionTitle?: InjectableCss;
    value?: TypographyPresentationProps;
    hr?: InjectableCss;
    billingAddress?: BillingAddressInfoDisplayStyles;
}

export const shippingInfoStyles: CheckoutReviewAndSubmitShippingInfoStyles = {
    shippingInfoAccordionSection: {
        titleTypographyProps: { weight: 600 },
    },
    warningSection: {
        css: css`
            display: flex;
            align-items: center;
            border: 2px solid ${getColor("danger.main")};
            padding: 15px 10px;
            margin-bottom: 10px;
        `,
    },
    warningIcon: {
        src: "AlertCircle",
        color: "danger.main",
        css: css`
            margin-right: 10px;
        `,
    },
};

const StyledSection = getStyledWrapper("section");
const styles = shippingInfoStyles;

const CheckoutReviewAndSubmitShippingInfo = ({
    shippingPageNavLink,
    shipToState,
    billToState,
    loadShipTo,
    cart,
    fulfillmentMethod,
    pickUpWarehouse,
    history,
    loadBillTo,
    cartId,
    // XNG-Changes
    poNumber,
    jobName,
    paymentMethodValue,
}: Props) => {
    useEffect(() => {
        if (!shipToState.value && !shipToState.isLoading && cart && cart.billToId && cart.shipToId) {
            loadShipTo({ billToId: cart.billToId, shipToId: cart.shipToId });
        }
    }, [shipToState]);

    useEffect(() => {
        if (!billToState.value && !billToState.isLoading && cart && cart.billToId) {
            loadBillTo({ billToId: cart.billToId });
        }
    }, [billToState]);

    if (!cart || !shipToState.value || !billToState.value) {
        return null;
    }

    const goBackToShipping = () => {
        if (!shippingPageNavLink) {
            return;
        }

        const backUrl = cartId ? `${shippingPageNavLink.url}?cartId=${cartId}` : shippingPageNavLink.url;
        return history.push(backUrl);
    };

    const sectionTitle =
        fulfillmentMethod === FulfillmentMethod.Ship ? "Billing & Shipping Information" : "Pick Up Location";
    // XNG-Changes
    const { carrier, shipVia, requestedDeliveryDate, requestedPickupDate } = cart;

    // XNG-Changes: PAC-707
    //  const showWarning = !billToState.value.address1 || !shipToState.value.address1;

    return (
        <StyledWrapper {...styles.wrapper}>
            {/** XNG-Changes: PAC-707 */}
            {/* {showWarning && (
                <StyledSection {...styles.warningSection}>
                    <Icon {...styles.warningIcon}></Icon>
                    <Typography {...styles.warningText}>
                        {siteMessage("ReviewAndPay_EnterBillingShippingInfo")}
                    </Typography>
                </StyledSection>
            )} */}
            {fulfillmentMethod === FulfillmentMethod.Ship && (
                <BillingAndShippingInfo
                    billTo={billToState.value}
                    shipTo={shipToState.value}
                    carrier={carrier!}
                    shipVia={shipVia!}
                    // XNG-Changes
                    deliveryDate={requestedDeliveryDate ? new Date(requestedDeliveryDate) : null}
                    onEditBillTo={goBackToShipping}
                    onEditShipTo={goBackToShipping}
                    // XNG-Changes
                    {...{ deliverAnytime: parseBoolean(cart.properties.deliverAnytime) || false }}
                />
            )}
            {fulfillmentMethod === FulfillmentMethod.PickUp && (
                <PickUpLocation
                    location={pickUpWarehouse!}
                    billTo={billToState.value}
                    // XNG-Changes
                    pickUpDate={requestedPickupDate ? new Date(requestedPickupDate) : null}
                    onEditLocation={goBackToShipping}
                    onEditBillTo={goBackToShipping}
                    // XNG-Changes
                    {...{ deliverAnytime: parseBoolean(cart.properties.deliverAnytime) || false, notes: cart.notes }}
                />
            )}

            {!cart.requiresApproval && (
                <>
                    {(poNumber || jobName) && (
                        <>
                            <StyledWrapper {...styles.hr} {...{ addMargin: true }} />
                            <StyledWrapper {...styles.container}>
                                <StyledWrapper {...styles.section}>
                                    <StyledWrapper {...styles.sectionTwoColums}>
                                        {poNumber && (
                                            <StyledWrapper>
                                                <Typography {...styles.sectionTitle}>
                                                    {translate("Purchase Order #")}
                                                </Typography>
                                                <Typography {...styles.value}>{poNumber}</Typography>
                                            </StyledWrapper>
                                        )}
                                        {jobName && (
                                            <StyledWrapper>
                                                <Typography {...styles.sectionTitle}>
                                                    {translate("Job Name / #")}
                                                </Typography>
                                                <Typography {...styles.value}>{jobName}</Typography>
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        </>
                    )}
                    <StyledWrapper {...styles.hr} {...{ addMargin: true }} />
                    <StyledWrapper {...styles.container}>
                        <StyledWrapper {...styles.section}>
                            <SectionTitle
                                title={translate("Payment")}
                                extraChild={<CheckoutMoveToStep moveToStep={CHECKOUT_STEPS.PAYMENT} />}
                                border="none"
                            />
                            <Typography {...styles.sectionTitle}>
                                {cart.paymentMethod?.isCreditCard || cart.paymentMethod?.isPaymentProfile
                                    ? translate("Credit Card")
                                    : cart.paymentMethod?.description}
                            </Typography>
                            {paymentMethodValue && (
                                <Typography {...styles.value}>{parse(paymentMethodValue, parserOptions)}</Typography>
                            )}
                        </StyledWrapper>
                        {fulfillmentMethod === FulfillmentMethod.PickUp && (
                            <StyledWrapper {...styles.section}>
                                <SectionTitle
                                    title={translate("Addresses")}
                                    extraChild={<CheckoutMoveToStep moveToStep={CHECKOUT_STEPS.SHIPPING} />}
                                    border="none"
                                />
                                <BillingAddressInfoDisplay
                                    billTo={billToState.value}
                                    onEdit={goBackToShipping}
                                    extendedStyles={styles.billingAddress}
                                />
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutReviewAndSubmitShippingInfo)),
    definition: {
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
        displayName: "Shipping Info",
    },
};

export default widgetModule;
